import { useMemo, useState, type CSSProperties } from 'react';
import { useLocale } from 'use-intl';
import { isolateBidi, useTranslations } from '@/i18n/translations';
import {
  AvatarStack,
  Button,
  Caption,
  Card,
  DotSep,
  I,
  Icon,
  Label,
  Pill,
  Pressable,
  Row,
  Stack,
  VisuallyHidden,
} from '@leanin/ui';
import type { PollView, PollVoterView } from '@leanin/shared';

import styles from './PollCard.module.css';
import { useOptionalViewer } from '../../lib/viewer-context';
import { pollTimeLeft } from './poll-time-left';
import { PollVotersModal } from './PollVotersModal';

function pct(count: number, total: number): number {
  return total === 0 ? 0 : Math.round((count / total) * 100);
}

const ENDS_IN_KEY = {
  day: 'poll.endsInDays',
  hour: 'poll.endsInHours',
  minute: 'poll.endsInMinutes',
} as const;

/** Per-row reveal stagger, so fills sweep top-to-bottom rather than all at once. */
const ROW_STAGGER_MS = 70;

/**
 * Read view of a post's single-choice poll. Every option is one capsule row
 * driven by `poll.results` (the server reveals results only once the viewer
 * has voted, the poll has closed, or the viewer wrote it — `isPollRevealed`).
 *
 * While the poll is OPEN ("Bubble") each row is a vote button that stays
 * mounted in both states:
 *
 *  - HIDDEN (`results === null`): empty rows with a selection ring.
 *  - REVEALED: each row's fill grows in place to its share, a check pops into
 *    the viewer's ring, and the percentage (plus, on a `public` poll, the
 *    previewed voters' faces) arrives just after. See PollCard.module.css.
 *
 * Open-poll options are toggle buttons (`aria-pressed`), not radios: a radio
 * group selects on arrow-key focus, which here would cast (and re-cast) votes
 * just by moving through the list. Tapping another option changes the vote;
 * tapping your own is a no-op. Rows use `aria-disabled` (not `disabled`)
 * while a vote is in flight so keyboard focus stays put.
 *
 * Once the poll is CLOSED the rows can no longer take a vote, so they stop
 * looking like controls ("settled bars"): no outline, no selection ring, the
 * winner read by a deeper fill and burgundy label, the viewer's own pick by a
 * small check and a visible "Your vote". A public poll's settled rows each
 * name who chose them and open that option's voter roster; an anonymous
 * poll's are plain results.
 *
 * The header pills say when voting closes and whether votes are public —
 * the voter-facing disclosure BEFORE they vote. After close that disclosure
 * moves into the footer beside "Final results". A persistent off-screen live
 * region announces the tally summary when results land.
 */
export function PollCard({
  postId,
  poll,
  question,
  onVote,
  pending,
}: {
  postId: string;
  poll: PollView;
  /** The post body — the poll's accessible name for the option group. */
  question: string;
  onVote: (optionId: string) => void;
  pending: boolean;
}) {
  const t = useTranslations('Feed');
  const locale = useLocale();
  const viewerId = useOptionalViewer()?.id ?? null;
  const nameList = useMemo(
    () => new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }),
    [locale],
  );

  const revealed = poll.results !== null;
  const total = poll.results?.totalVotes ?? 0;
  const tallyFor = (id: string) =>
    poll.results?.tallies.find((tally) => tally.optionId === id);
  // Only a public poll's tallies carry voters; an anonymous poll's tally type
  // has no voter field.
  const votersFor = (id: string): PollVoterView[] =>
    poll.visibility === 'public'
      ? (poll.results?.tallies.find((tally) => tally.optionId === id)?.voters ?? [])
      : [];
  // Highest tally, floored at 0 so an empty closed poll marks no leader. Ties
  // mark every co-leader — "Most votes" is a factual statement per option.
  const leadCount = revealed
    ? Math.max(0, ...poll.options.map((o) => tallyFor(o.id)?.voteCount ?? 0))
    : 0;

  const statusText = revealed
    ? poll.closed
      ? t('poll.closedTotal', { count: total })
      : t('poll.openTotal', { count: total })
    : t('poll.voteToReveal');

  // `closesAt` is on the wire; `Date.now()` is read here (the boundary) and
  // passed into the pure `pollTimeLeft`.
  const timeLeft = poll.closed ? null : pollTimeLeft(poll.closesAt, Date.now());
  const canChangeVote = !poll.closed && poll.viewerVotedOptionId !== null;
  // The voters sheet and the roster it shows: the whole poll (`optionId: null`)
  // or one option's voters, from a settled public row.
  const [votersSheet, setVotersSheet] = useState<{ open: boolean; optionId: string | null }>({
    open: false,
    optionId: null,
  });
  const votersResults =
    poll.visibility === 'public' && poll.results !== null && poll.results.totalVotes > 0
      ? poll.results
      : null;

  return (
    <Card pad="md" bordered={false}>
      <Stack gap="md">
        <VisuallyHidden role="status" aria-live="polite">
          {revealed ? statusText : ''}
        </VisuallyHidden>

        {poll.closed ? null : (
          <Row gap="xs" align="center" wrap>
            {timeLeft ? (
              <Pill size="sm" icon={I.clock}>
                {t(ENDS_IN_KEY[timeLeft.unit], { count: timeLeft.count })}
              </Pill>
            ) : null}
            {poll.visibility === 'public' ? (
              <Pill size="sm" icon={I.users}>
                {t('poll.publicVotes')}
              </Pill>
            ) : (
              <Pill size="sm" icon={I.lock}>
                {t('poll.anonymousVoting')}
              </Pill>
            )}
          </Row>
        )}

        <div role="group" aria-label={question} className={styles.options}>
          {poll.options.map((o, i) => {
            const tally = tallyFor(o.id);
            const count = tally?.voteCount ?? 0;
            const share = pct(count, total);
            const mine = poll.viewerVotedOptionId === o.id;
            const lead = revealed && count === leadCount && count > 0;
            const voters = votersFor(o.id);
            if (poll.closed) {
              return (
                <SettledOption
                  key={o.id}
                  label={o.label}
                  count={count}
                  share={share}
                  mine={mine}
                  lead={lead}
                  delay={i * ROW_STAGGER_MS}
                  roster={
                    poll.visibility === 'public' && count > 0
                      ? {
                          onOpen: () => setVotersSheet({ open: true, optionId: o.id }),
                          preview:
                            voters.length > 0
                              ? {
                                  voters,
                                  names: voterSummary({
                                    voters,
                                    count,
                                    viewerId,
                                    you: t('poll.you'),
                                    others: (n) => t('poll.othersCount', { count: n }),
                                    nameList,
                                  }),
                                }
                              : null,
                        }
                      : null
                  }
                />
              );
            }
            return (
              <Pressable
                key={o.id}
                className={styles.row}
                aria-pressed={mine}
                aria-disabled={pending || undefined}
                data-revealed={revealed || undefined}
                data-lead={lead || undefined}
                style={
                  {
                    '--share': share / 100,
                    '--delay': `${i * ROW_STAGGER_MS}ms`,
                  } as CSSProperties
                }
                onClick={() => {
                  if (pending || mine) return;
                  onVote(o.id);
                }}
              >
                <span className={styles.fill} aria-hidden="true" />
                <span className={styles.mark} aria-hidden="true">
                  <Icon size={12}>{I.check}</Icon>
                </span>
                <span className={styles.label}>
                  <Label>{o.label}</Label>
                </span>
                {revealed && voters.length > 0 ? (
                  <span className={styles.voters} aria-hidden="true">
                    <AvatarStack
                      size={24}
                      max={voters.length}
                      people={voters.map((v) => ({
                        name: v.firstName,
                        ...(v.photoUrl ? { photo: v.photoUrl } : {}),
                      }))}
                    />
                  </span>
                ) : null}
                {revealed ? (
                  <span className={styles.share}>
                    <Label
                      style={{
                        fontVariantNumeric: 'tabular-nums',
                        color: lead ? 'var(--palette-burgundy-deep)' : 'var(--muted-foreground)',
                      }}
                    >
                      {t('poll.percent', { pct: share })}
                    </Label>
                    <VisuallyHidden>
                      {', '}
                      {t('poll.optionVotes', { count })}
                      {lead ? `, ${t('poll.mostVotes')}` : ''}
                      {voters.length > 0
                        ? `, ${t('poll.votedBy', { names: voters.map((v) => v.firstName).join(', ') })}`
                        : ''}
                    </VisuallyHidden>
                  </span>
                ) : null}
              </Pressable>
            );
          })}
        </div>

        <Row gap="xs" align="center" wrap>
          <Caption>{statusText}</Caption>
          {poll.closed ? (
            <>
              <DotSep />
              <Caption>
                {poll.visibility === 'public' ? t('poll.publicVotes') : t('poll.anonymousVoting')}
              </Caption>
            </>
          ) : null}
          {canChangeVote ? (
            <>
              <DotSep />
              <Caption>{t('poll.changeVoteHint')}</Caption>
            </>
          ) : null}
          {votersResults !== null ? (
            <>
              <DotSep />
              <Button variant="link" size="sm" onClick={() => setVotersSheet({ open: true, optionId: null })}>
                {t('poll.seeVoters')}
              </Button>
            </>
          ) : null}
        </Row>
      </Stack>
      {votersResults !== null ? (
        <PollVotersModal
          postId={postId}
          options={poll.options}
          results={votersResults}
          optionId={votersSheet.optionId}
          onOptionChange={(optionId) => setVotersSheet({ open: true, optionId })}
          open={votersSheet.open}
          onOpenChange={(open) => setVotersSheet((sheet) => ({ ...sheet, open }))}
        />
      ) : null}
    </Card>
  );
}

/**
 * "You, Uma, and 3 others" — the previewed voters (the server lists the
 * viewer first) plus however many the preview left out, which also covers
 * votes from accounts the roster never lists.
 */
function voterSummary({
  voters,
  count,
  viewerId,
  you,
  others,
  nameList,
}: {
  voters: PollVoterView[];
  count: number;
  viewerId: string | null;
  you: string;
  others: (n: number) => string;
  nameList: Intl.ListFormat;
}): string {
  const names = voters.map((v) => (v.userId === viewerId ? you : isolateBidi(v.firstName)));
  const unnamed = count - voters.length;
  if (unnamed > 0) names.push(others(unnamed));
  return nameList.format(names);
}

/**
 * One option of a closed poll. The capsule is the open row's shape and height
 * (the card itself still changes on close: the header pills go, and a public
 * option gains its "who chose this" line). A public option with votes is one
 * button — bar plus that line — opening the option's voter roster.
 */
function SettledOption({
  label,
  count,
  share,
  mine,
  lead,
  delay,
  roster,
}: {
  label: string;
  count: number;
  share: number;
  mine: boolean;
  lead: boolean;
  delay: number;
  /** Public option with votes: opens its voter roster, and names the
   *  previewed voters when the roster lists any. `null` for an anonymous poll
   *  or an option nobody chose. */
  roster: {
    onOpen: () => void;
    preview: { voters: PollVoterView[]; names: string } | null;
  } | null;
}) {
  const t = useTranslations('Feed');

  const bar = (
    <span
      className={styles.row}
      data-closed
      data-revealed
      data-lead={lead || undefined}
      style={{ '--share': share / 100, '--delay': `${delay}ms` } as CSSProperties}
    >
      <span className={styles.fill} aria-hidden="true" />
      <span className={styles.label}>
        <Label>{label}</Label>
        {mine ? (
          <span className={styles.mine}>
            <span aria-hidden="true">
              <Icon size={12}>{I.check}</Icon>
            </span>
            <Label size="sm">{t('poll.yourVote')}</Label>
          </span>
        ) : null}
      </span>
      <span className={styles.share}>
        <Label
          style={{
            fontVariantNumeric: 'tabular-nums',
            color: lead ? 'var(--palette-burgundy-deep)' : 'var(--muted-foreground)',
          }}
        >
          {t('poll.percent', { pct: share })}
        </Label>
        <VisuallyHidden>
          {', '}
          {t('poll.optionVotes', { count })}
          {lead ? `, ${t('poll.mostVotes')}` : ''}
        </VisuallyHidden>
      </span>
    </span>
  );

  const preview = roster?.preview ?? null;
  const voterLine =
    preview !== null ? (
      <span className={styles.voterLine}>
        <span aria-hidden="true">
          <AvatarStack
            size={24}
            max={preview.voters.length}
            people={preview.voters.map((v) => ({
              name: v.firstName,
              ...(v.photoUrl ? { photo: v.photoUrl } : {}),
            }))}
          />
        </span>
        <Caption>{preview.names}</Caption>
      </span>
    ) : null;

  if (roster === null) {
    return <div className={styles.settled}>{bar}</div>;
  }
  return (
    <Pressable className={styles.settled} aria-haspopup="dialog" onClick={roster.onOpen}>
      {bar}
      {voterLine}
    </Pressable>
  );
}
